(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class StitchdEditorialHero extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-sv3-hero-slide]')];
      this.dots = [...this.querySelectorAll('[data-sv3-hero-dot]')];
      this.previous = this.querySelector('[data-sv3-hero-prev]');
      this.next = this.querySelector('[data-sv3-hero-next]');
      this.pause = this.querySelector('[data-sv3-hero-pause]');
      this.live = this.querySelector('[data-sv3-hero-live]');
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.duration = Number(this.dataset.autoplay) || 7000;
      this.manuallyPaused = false;
      this.temporaryPaused = false;
      this.timer = null;
      this.touchStartX = null;
      this.style.setProperty('--sv3-hero-duration', `${this.duration}ms`);

      this.onPrevious = () => this.show(this.index - 1, true);
      this.onNext = () => this.show(this.index + 1, true);
      this.onPause = () => {
        this.manuallyPaused = !this.manuallyPaused;
        this.updatePause();
        this.schedule();
      };
      this.onKeydown = (event) => {
        if (event.key === 'ArrowLeft') this.onPrevious();
        if (event.key === 'ArrowRight') this.onNext();
      };
      this.onVisibility = () => this.schedule();
      this.onMotionChange = () => this.schedule();
      this.onTouchStart = (event) => { this.touchStartX = event.changedTouches[0]?.clientX ?? null; };
      this.onTouchEnd = (event) => {
        if (this.touchStartX === null) return;
        const distance = (event.changedTouches[0]?.clientX ?? this.touchStartX) - this.touchStartX;
        this.touchStartX = null;
        if (Math.abs(distance) < 45) return;
        if (distance > 0) this.onPrevious(); else this.onNext();
      };
      this.onEditorSelect = (event) => {
        const selected = event.target.closest('[data-sv3-hero-slide]');
        if (!selected || !this.contains(selected)) return;
        this.temporaryPaused = true;
        this.show(this.slides.indexOf(selected), true);
      };

      this.previous?.addEventListener('click', this.onPrevious);
      this.next?.addEventListener('click', this.onNext);
      this.pause?.addEventListener('click', this.onPause);
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.show(Number(dot.dataset.sv3HeroDot), true)));
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('mouseenter', () => { this.temporaryPaused = true; this.schedule(); });
      this.addEventListener('mouseleave', () => { this.temporaryPaused = false; this.schedule(); });
      this.addEventListener('focusin', () => { this.temporaryPaused = true; this.schedule(); });
      this.addEventListener('focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.temporaryPaused = false;
        this.schedule();
      });
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('shopify:block:select', this.onEditorSelect);
      reduceMotion.addEventListener?.('change', this.onMotionChange);

      this.update();
      this.schedule();
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      this.previous?.removeEventListener('click', this.onPrevious);
      this.next?.removeEventListener('click', this.onNext);
      this.pause?.removeEventListener('click', this.onPause);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchend', this.onTouchEnd);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onEditorSelect);
      reduceMotion.removeEventListener?.('change', this.onMotionChange);
    }

    canPlay() {
      return this.slides.length > 1 && !this.manuallyPaused && !this.temporaryPaused && !reduceMotion.matches && !document.hidden;
    }

    show(index, announce = false) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, position) => {
        const active = position === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if ('inert' in slide) slide.inert = !active;
      });
      this.update();
      if (announce && this.live) this.live.textContent = `Slide ${this.index + 1} of ${this.slides.length}`;
      this.schedule();
    }

    update() {
      this.dots.forEach((dot, position) => {
        const active = position === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.updatePause();
    }

    updatePause() {
      if (!this.pause) return;
      const paused = this.manuallyPaused || reduceMotion.matches;
      this.pause.setAttribute('aria-pressed', paused ? 'true' : 'false');
      this.pause.setAttribute('aria-label', paused ? 'Play slides' : 'Pause slides');
      const icon = this.pause.querySelector('span');
      if (icon) icon.textContent = paused ? '▶' : 'Ⅱ';
    }

    schedule() {
      window.clearTimeout(this.timer);
      this.classList.toggle('is-paused', !this.canPlay());
      if (!this.canPlay()) return;
      const activeDot = this.dots[this.index];
      if (activeDot) {
        activeDot.classList.remove('is-active');
        void activeDot.offsetWidth;
        activeDot.classList.add('is-active');
      }
      this.timer = window.setTimeout(() => this.show(this.index + 1), this.duration);
    }
  }

  if (!customElements.get('stitchd-editorial-hero')) {
    customElements.define('stitchd-editorial-hero', StitchdEditorialHero);
  }

  const reveal = (root = document) => {
    const items = [...root.querySelectorAll('[data-sv3-reveal]:not(.is-visible)')];
    if (!items.length) return;
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((item) => observer.observe(item));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => reveal());
  } else {
    reveal();
  }
  document.addEventListener('shopify:section:load', (event) => reveal(event.target));

  // Load fail-safe: if an observer misses its first paint during a slow/partial load,
  // reveal only elements already inside the viewport. Normal reveal behavior is unchanged.
  window.addEventListener('load', () => {
    reveal();
    window.setTimeout(() => {
      document.querySelectorAll('[data-sv3-reveal]:not(.is-visible)').forEach((item) => {
        const rect = item.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) item.classList.add('is-visible');
      });
    }, 120);
  }, { once: true });
})();
